import {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import { useWindowDimensions, Linking } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { useSharedValue, withTiming } from 'react-native-reanimated';

import { Task } from '@/student/types';
import { useAuthStore } from '@/common/auth/store';
import { useModuleAndTaskSelectionState } from '@/common/context/AppStateStore';
import { useActivity } from '@/common/hooks/student/useActivity';
import { useSelectedModule } from '@/common/hooks/student/useSelectedModule';
import { useDialog } from '@/common/context/DialogContext';
import { useCompleteModuleSubmissionUx } from '@/common/hooks/student/module/useCompleteModuleSubmissionUx';
import { isUUID } from '@/common/types/ids';
import log from '@/common/utils/logger';

/**
 * Custom hook for ModuleScreen logic.
 * Manages module/task state and submission logic.
 */
export const useModuleScreen = () => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigation = useNavigation<any>();
  const { showDialog, showToastMessage } = useDialog();
  const completeModuleSubmissionUx = useCompleteModuleSubmissionUx();
  const { width: windowWidth } = useWindowDimensions();

  const { user } = useAuthStore();

  const {
    module, tasks, isModulesLoading, isTasksLoading, submitModuleMutation,
  } = useSelectedModule();
  const { selectedModule, setSelectedTask } = useModuleAndTaskSelectionState();
  const { logSelectTask, logSubmitModule } = useActivity();

  const [selectedTaskIdLocal, setSelectedTaskIdLocal] = useState<string | null>(null);

  const selectedTask = useMemo(
    () => tasks?.find((t) => t.id === selectedTaskIdLocal) || null,
    [tasks, selectedTaskIdLocal],
  );

  const isMobile = windowWidth < 768;
  const drawerWidth = useSharedValue(0);
  const drawerTargetWidth = isMobile ? windowWidth : 480;

  useEffect(() => {
    drawerWidth.value = withTiming(selectedTaskIdLocal ? drawerTargetWidth : 0, { duration: 300 });
  }, [selectedTaskIdLocal, drawerTargetWidth, drawerWidth]);

  useEffect(() => {
    // Only redirect if we are definitely not loading AND we have no module selection in the store
    // This prevents race conditions during navigation transitions
    const hasModuleSelection = !!selectedModule?.moduleId;
    if (!isModulesLoading && !module && !hasModuleSelection) {
      navigation.navigate('CourseScreen');
    }
  }, [isModulesLoading, module, selectedModule?.moduleId, navigation]);

  const levels = useMemo(() => module?.metadata?.levels || [], [module]);
  const totalTasks = tasks?.length || 0;
  const completedCount = useMemo(
    () => tasks?.filter((t) => t.submission?.state === 'marked_as_done').length || 0,
    [tasks],
  );
  const allTasksCompleted = totalTasks > 0 && completedCount === totalTasks;

  const handleBack = useCallback(() => {
    navigation.navigate('CourseScreen');
  }, [navigation]);

  const handleConfirmModule = useCallback(() => {
    if (!allTasksCompleted) return;

    showDialog({
      title: t('student.module.submit-module-dialog.title'),
      message: t('student.module.submit-module-dialog.complete-message'),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('student.module.submit-module-dialog.button-submit'),
          action: 'positive',
          testID: 'button-dialog-submit-module',
          onPress: () => {
            if (!module?.id || !selectedModule?.courseId) return;

            logSubmitModule({
              moduleId: module.id,
              courseId: selectedModule.courseId,
            });

            submitModuleMutation.mutate({
              moduleId: module.id,
              courseId: selectedModule.courseId,
            }, {
              onSuccess: () => {
                completeModuleSubmissionUx({
                  moduleTitle: module.title,
                  courseId: selectedModule.courseId,
                  moduleId: module.id,
                });
              },
              onError: () => {
                showToastMessage('error', t('student.module.submit-module-dialog.error-message'));
              },
            });
          },
        },
      ],
    });
  }, [
    allTasksCompleted,
    showDialog,
    module?.id,
    module?.title,
    logSubmitModule,
    selectedModule?.courseId,
    submitModuleMutation,
    showToastMessage,
    completeModuleSubmissionUx,
    t,
  ]);

  const handleSelectTask = useCallback((task: Task) => {
    const moduleId = module?.id;
    if (!moduleId || !isUUID(moduleId)) {
      log.warn('[useModuleScreen] Invalid moduleId', { moduleId });
      return;
    }

    if (!isUUID(task.id)) {
      log.warn('[useModuleScreen] Invalid taskId', { taskId: task.id });
      return;
    }

    setSelectedTaskIdLocal(task.id);
    setSelectedTask(moduleId, task.id);
    logSelectTask({
      moduleId,
      courseId: selectedModule?.courseId || '',
      taskId: task.id,
    });
  }, [module?.id, setSelectedTask, logSelectTask, selectedModule?.courseId]);

  const handleCloseDrawer = useCallback(() => {
    const moduleId = module?.id;
    if (!moduleId || !isUUID(moduleId)) {
      log.warn('[useModuleScreen] Invalid moduleId in handleCloseDrawer', { moduleId });
      return;
    }

    setSelectedTaskIdLocal(null);
    setSelectedTask(moduleId, null);
  }, [module?.id, setSelectedTask]);

  const handleModuleUpgraded = useCallback(() => {
    setSelectedTaskIdLocal(null);
    const moduleId = module?.id;
    if (moduleId && isUUID(moduleId)) {
      setSelectedTask(moduleId, null);
    }
  }, [module?.id, setSelectedTask]);

  const handleOpenTask = useCallback((task: Task) => {
    // If it's an external link or specialized task, handle it here
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((task.type as string) === 'link' && (task as any).content?.url) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Linking.openURL((task as any).content.url);
      return;
    }
    // Otherwise navigate to the task screen
    navigation.navigate('TaskScreen', {
      moduleId: module?.id,
      courseId: selectedModule?.courseId,
      taskId: task.id,
    });
    logSelectTask({
      moduleId: module?.id || '',
      courseId: selectedModule?.courseId || '',
      taskId: task.id,
    });
  }, [module?.id, selectedModule?.courseId, navigation, logSelectTask]);

  return {
    module,
    tasks,
    isModulesLoading,
    isTasksLoading,
    selectedTask,
    selectedModule,
    isMobile,
    drawerWidth,
    drawerTargetWidth,
    levels,
    totalTasks,
    completedCount,
    allTasksCompleted,
    isSubmitting: submitModuleMutation.isPending,
    handleBack,
    handleConfirmModule,
    handleSelectTask,
    handleCloseDrawer,
    handleOpenTask,
    handleModuleUpgraded,
    user,
  };
};
